/**
 * 템플릿별 폼 명세.
 *
 * 손으로 5벌의 폼을 짜는 대신 **필드를 선언**하고 렌더러 하나가 그린다.
 * 그 대가로 얻는 것:
 *   - 글자수 제한·검증·오류 표시가 모든 필드에서 같게 동작한다
 *   - 템플릿이 늘 때 폼 컴포넌트가 아니라 명세만 추가하면 된다
 *   - 라벨·순서·도움말은 여전히 템플릿마다 손으로 정한다(자동 생성이 아니다)
 *
 * 글자수 상한은 `@signageone/core` 의 스키마와 맞춰 둔다. 사이니지는 멀리서 읽으므로
 * 상한이 곧 디자인 제약이고, 폼에서 미리 보여야 사용자가 잘린 뒤에 알지 않는다.
 */
import { ICON_NAMES, TEMPLATE_IDS, ZERO_ACCIDENT_MULTIPLIERS, type TemplateId } from '@signageone/core';

export type Field =
  | { kind: 'text'; path: string; label: string; max?: number; placeholder?: string; help?: string }
  | { kind: 'textarea'; path: string; label: string; max?: number; help?: string }
  | { kind: 'number'; path: string; label: string; min?: number; max?: number; help?: string }
  | { kind: 'boolean'; path: string; label: string; help?: string }
  | { kind: 'time'; path: string; label: string; help?: string }
  | { kind: 'datetime'; path: string; label: string; help?: string }
  /** 날짜 하나 — `YYYY-MM-DD`(2026-11-03 무재해 현황의 시작일). 시각이 없어 시간대에 따라 하루 밀리지 않는다. */
  | { kind: 'date'; path: string; label: string; help?: string }
  | { kind: 'select'; path: string; label: string; options: ReadonlyArray<{ value: string; label: string }>; help?: string }
  | { kind: 'icon'; path: string; label: string }
  /**
   * 콘텐츠로 올린 그림·영상·HTML 하나 — `{ assetId, kind, fit }` 또는 null(2026-10-30 반반 화면, HTML 은 2026-11-03). 콘텐츠에서 고르거나 내 컴퓨터에서
   * 올린다. 맞춤(가득 채우기·전체 보이기)도 이 칸이 함께 다룬다.
   */
  | { kind: 'media'; path: string; label: string; help?: string }
  /**
   * 구역 나누기의 모양과 구역들(2026-11-03) — `layout`·`zones` 를 함께 다룬다(모양을 바꾸면 구역 수가 따라 바뀐다). 경로는 비워
   * 데이터 전체를 받는다.
   */
  | { kind: 'zones'; path: ''; label: string; help?: string }
  /** 쉼표로 끊어 입력하는 목록. 월 이름·수치처럼 짧은 값이 줄지어 있을 때. */
  | { kind: 'csv'; path: string; label: string; numeric?: boolean; help?: string }
  /** 문자열 배열 — 한 줄에 하나씩. */
  | { kind: 'stringList'; path: string; label: string; max: number; itemMax: number; itemLabel: string; help?: string }
  /** 객체 배열 — 항목마다 하위 필드를 반복해 그린다. */
  | {
      kind: 'objectList';
      path: string;
      label: string;
      max: number;
      itemLabel: string;
      fields: readonly Field[];
      /** 새 항목의 초기값. */
      blank: () => unknown;
      help?: string;
    }
  /** 켜고 끌 수 있는 묶음 — 끄면 `null` 이 되어 화면에서 사라진다. */
  | {
      kind: 'group';
      path: string;
      label: string;
      fields: readonly Field[];
      /** 켤 때의 초기값. */
      blank: () => unknown;
      help?: string;
    };

export interface FormSection {
  title: string;
  help?: string;
  fields: readonly Field[];
}

const TONES = [
  { value: 'blue', label: '파랑' },
  { value: 'green', label: '초록' },
  { value: 'purple', label: '보라' },
  { value: 'orange', label: '주황' },
] as const;

/** 아이콘 선택지. core 의 목록이 진실원천이라 아이콘이 늘면 자동으로 따라온다. */
export const ICON_OPTIONS = ICON_NAMES.map((name) => ({ value: name, label: name }));

/** 모든 템플릿이 갖는 하단 티커. */
const tickerSection: FormSection = {
  title: '하단 티커',
  help: '여러 소식은 " | " 로 구분해 이어 씁니다.',
  fields: [
    { kind: 'text', path: 'ticker.label', label: '라벨', max: 12, placeholder: 'NEWS' },
    { kind: 'textarea', path: 'ticker.content', label: '내용', max: 600 },
  ],
};

export const TEMPLATE_FORMS: Readonly<Record<TemplateId, readonly FormSection[]>> = {
  announcement: [
    {
      title: '주 내용',
      help: '화면 왼쪽 큰 카드에 표시됩니다.',
      fields: [
        { kind: 'text', path: 'badge', label: '배지', max: 20, placeholder: '인사공지' },
        { kind: 'text', path: 'title', label: '제목', max: 80 },
        { kind: 'textarea', path: 'body', label: '본문', max: 400, help: '한두 문장을 권장합니다. 길면 멀리서 읽기 어렵습니다.' },
        { kind: 'text', path: 'date', label: '날짜', max: 20, placeholder: '2026.06.15' },
        { kind: 'text', path: 'author', label: '작성 부서', max: 30 },
      ],
    },
    {
      title: '최근 공지',
      help: '화면 오른쪽 목록입니다. 비우면 표시되지 않습니다.',
      fields: [
        {
          kind: 'objectList',
          path: 'notices',
          label: '공지 목록',
          max: 6,
          itemLabel: '공지',
          blank: () => ({ title: '', dept: '' }),
          fields: [
            { kind: 'text', path: 'title', label: '제목', max: 60 },
            { kind: 'text', path: 'dept', label: '부서', max: 20 },
          ],
        },
      ],
    },
    {
      title: '날씨',
      fields: [
        {
          kind: 'group',
          path: 'weather',
          label: '날씨 카드 표시',
          blank: () => ({ temperature: '18°C', condition: '맑음', place: '본사' }),
          fields: [
            { kind: 'text', path: 'temperature', label: '기온', max: 10 },
            { kind: 'text', path: 'condition', label: '날씨', max: 20 },
            { kind: 'text', path: 'place', label: '지역', max: 30 },
          ],
        },
      ],
    },
    tickerSection,
  ],

  promotion: [
    {
      title: '주 내용',
      help: '화면 왼쪽 그라데이션 영역에 표시됩니다.',
      fields: [
        { kind: 'text', path: 'badge', label: '배지', max: 30 },
        { kind: 'text', path: 'title', label: '제목', max: 60 },
        {
          kind: 'text',
          path: 'titleAccent',
          label: '강조 제목',
          max: 60,
          help: '제목 다음 줄에 노란색으로 표시됩니다. 비우면 제목만 나옵니다.',
        },
        { kind: 'textarea', path: 'body', label: '본문', max: 300 },
      ],
    },
    {
      title: '지원금',
      fields: [
        { kind: 'text', path: 'amountLabel', label: '라벨', max: 30 },
        { kind: 'text', path: 'amount', label: '금액', max: 30, placeholder: '최대 200만원' },
      ],
    },
    {
      title: '혜택',
      fields: [
        {
          kind: 'objectList',
          path: 'benefits',
          label: '혜택 목록',
          max: 4,
          itemLabel: '혜택',
          blank: () => ({ icon: 'star', title: '', description: '' }),
          fields: [
            { kind: 'icon', path: 'icon', label: '아이콘' },
            { kind: 'text', path: 'title', label: '제목', max: 40 },
            { kind: 'text', path: 'description', label: '설명', max: 60 },
          ],
        },
      ],
    },
    {
      title: '마감 카운트다운',
      fields: [
        {
          kind: 'datetime',
          path: 'deadline',
          label: '마감 시각',
          help: '비우면 카운트다운이 표시되지 않습니다. 남은 시간은 초 단위로 표시됩니다.',
        },
        { kind: 'text', path: 'deadlineLabel', label: '라벨', max: 30 },
      ],
    },
    {
      title: 'QR 안내',
      help: 'QR 은 장식용 패턴이며 실제로 스캔되지 않습니다.',
      fields: [
        {
          kind: 'group',
          path: 'qr',
          label: 'QR 영역 표시',
          blank: () => ({ caption: 'QR코드로 신청', hint: '카메라로 스캔하세요' }),
          fields: [
            { kind: 'text', path: 'caption', label: '제목', max: 30 },
            { kind: 'text', path: 'hint', label: '설명', max: 40 },
          ],
        },
      ],
    },
    tickerSection,
  ],

  alert: [
    {
      title: '알림',
      help: '즉시 조치가 필요한 상황에 씁니다.',
      fields: [
        { kind: 'text', path: 'label', label: '라벨', max: 20, placeholder: '긴급 알림' },
        { kind: 'text', path: 'iconLabel', label: '아이콘 아래 문구', max: 20, placeholder: '시스템 점검' },
        { kind: 'text', path: 'title', label: '제목', max: 80 },
      ],
    },
    {
      title: '시간 배너',
      fields: [
        {
          kind: 'objectList',
          path: 'stats',
          label: '수치',
          max: 2,
          itemLabel: '항목',
          blank: () => ({ label: '', value: '' }),
          fields: [
            { kind: 'text', path: 'label', label: '라벨', max: 20 },
            { kind: 'text', path: 'value', label: '값', max: 30 },
          ],
        },
      ],
    },
    {
      title: '행동 지침',
      help: '번호는 자동으로 붙습니다.',
      fields: [
        {
          kind: 'stringList',
          path: 'actions',
          label: '지침',
          max: 4,
          itemMax: 60,
          itemLabel: '지침',
        },
      ],
    },
    tickerSection,
  ],

  schedule: [
    {
      title: '일정',
      fields: [
        { kind: 'text', path: 'heading', label: '제목', max: 30, placeholder: '오늘의 일정' },
        {
          kind: 'objectList',
          path: 'events',
          label: '일정 목록',
          max: 6,
          itemLabel: '일정',
          blank: () => ({ time: '09:00', title: '', location: '' }),
          fields: [
            { kind: 'time', path: 'time', label: '시각' },
            { kind: 'text', path: 'title', label: '제목', max: 60 },
            { kind: 'text', path: 'location', label: '장소', max: 60 },
          ],
        },
      ],
    },
    {
      title: 'D-day 배지',
      fields: [
        {
          kind: 'group',
          path: 'countdown',
          label: 'D-day 표시',
          blank: () => ({ label: 'D-7', title: '' }),
          fields: [
            { kind: 'text', path: 'label', label: '라벨', max: 20, placeholder: 'D-7' },
            { kind: 'text', path: 'title', label: '내용', max: 30 },
          ],
        },
      ],
    },
    {
      title: '다가오는 이벤트',
      fields: [
        {
          kind: 'group',
          path: 'upcoming',
          label: '이벤트 카드 표시',
          blank: () => ({ label: '다가오는 이벤트', title: '', items: [] }),
          fields: [
            { kind: 'text', path: 'label', label: '라벨', max: 30 },
            { kind: 'text', path: 'title', label: '행사 이름', max: 40 },
            {
              kind: 'objectList',
              path: 'items',
              label: '세부 정보',
              max: 4,
              itemLabel: '항목',
              blank: () => ({ icon: 'calendar', text: '' }),
              fields: [
                { kind: 'icon', path: 'icon', label: '아이콘' },
                { kind: 'text', path: 'text', label: '내용', max: 60 },
              ],
            },
          ],
        },
      ],
    },
    {
      title: '주간 통계',
      fields: [
        {
          kind: 'group',
          path: 'stats',
          label: '통계 카드 표시',
          blank: () => ({ heading: '이번 주 일정', items: [] }),
          fields: [
            { kind: 'text', path: 'heading', label: '제목', max: 30 },
            {
              kind: 'objectList',
              path: 'items',
              label: '수치',
              max: 4,
              itemLabel: '수치',
              blank: () => ({ value: '0', label: '' }),
              fields: [
                { kind: 'text', path: 'value', label: '값', max: 10 },
                { kind: 'text', path: 'label', label: '라벨', max: 20 },
              ],
            },
          ],
        },
      ],
    },
    tickerSection,
  ],

  dashboard: [
    {
      title: 'KPI',
      help: '색은 아이콘 배경에만 쓰이고, 상승·하락은 화살표로 표시됩니다.',
      fields: [
        {
          kind: 'objectList',
          path: 'kpis',
          label: 'KPI 목록',
          max: 4,
          itemLabel: 'KPI',
          blank: () => ({ label: '', value: '', change: '', up: true, icon: 'dollar', tone: 'blue' }),
          fields: [
            { kind: 'text', path: 'label', label: '이름', max: 30 },
            { kind: 'text', path: 'value', label: '값', max: 20 },
            { kind: 'text', path: 'change', label: '증감', max: 20, placeholder: '+12.5% 전월 대비' },
            { kind: 'boolean', path: 'up', label: '상승' },
            { kind: 'icon', path: 'icon', label: '아이콘' },
            { kind: 'select', path: 'tone', label: '색', options: TONES },
          ],
        },
      ],
    },
    {
      title: '막대 차트',
      help: '값은 0~100 비율로 넣습니다. 축 눈금이 없어 상대 비율만 보입니다.',
      fields: [
        { kind: 'text', path: 'chart.title', label: '제목', max: 40 },
        { kind: 'csv', path: 'chart.categories', label: '가로축', help: '쉼표로 구분합니다. 예: 7월, 8월, 9월' },
        {
          kind: 'objectList',
          path: 'chart.series',
          label: '계열',
          max: 2,
          itemLabel: '계열',
          blank: () => ({ name: '', tone: 'blue', values: [] }),
          fields: [
            { kind: 'text', path: 'name', label: '이름', max: 20 },
            { kind: 'select', path: 'tone', label: '색', options: TONES },
            { kind: 'csv', path: 'values', label: '값 (0~100)', numeric: true, help: '쉼표로 구분하며, 가로축 항목 수와 같아야 합니다.' },
          ],
        },
      ],
    },
    {
      title: '순위표',
      fields: [
        {
          kind: 'group',
          path: 'leaderboard',
          label: '순위표 표시',
          blank: () => ({ title: '순위', live: false, rows: [] }),
          fields: [
            { kind: 'text', path: 'title', label: '제목', max: 30 },
            { kind: 'boolean', path: 'live', label: '실시간 표시', help: '켜면 머리글에 LIVE 표시가 함께 나옵니다.' },
            {
              kind: 'objectList',
              path: 'rows',
              label: '순위',
              max: 5,
              itemLabel: '행',
              blank: () => ({ name: '', dept: '', score: '' }),
              fields: [
                { kind: 'text', path: 'name', label: '이름', max: 30 },
                { kind: 'text', path: 'dept', label: '소속', max: 30 },
                { kind: 'text', path: 'score', label: '실적', max: 20 },
              ],
            },
          ],
        },
      ],
    },
    {
      title: '진행률',
      fields: [
        {
          kind: 'group',
          path: 'progress',
          label: '진행률 표시',
          blank: () => ({ title: '진행률', rows: [] }),
          fields: [
            { kind: 'text', path: 'title', label: '제목', max: 30 },
            {
              kind: 'objectList',
              path: 'rows',
              label: '항목',
              max: 5,
              itemLabel: '항목',
              blank: () => ({ name: '', percent: 50 }),
              fields: [
                { kind: 'text', path: 'name', label: '이름', max: 40 },
                { kind: 'number', path: 'percent', label: '퍼센트', min: 0, max: 100 },
              ],
            },
          ],
        },
      ],
    },
    tickerSection,
  ],

  split: [
    {
      title: '그림·영상·HTML',
      help: '화면의 반에 꽉 차게 나옵니다. 영상은 소리 없이 되풀이합니다. 올린 HTML 도 넣을 수 있습니다 — 그 칸에서 스크립트까지 돌고, HTML 에 넣은 영상도 소리 없이 나옵니다. 이 템플릿은 에이전트를 새 판으로 올린 화면에만 나갑니다(옛 판 화면은 건너뜁니다). HTML 을 넣은 것은 HTML 칸을 그릴 줄 아는 판이어야 합니다.',
      fields: [
        {
          kind: 'media',
          path: 'media',
          label: '그림·영상·HTML',
          help: '콘텐츠에 올린 이미지·영상·HTML 에서 고르거나 내 컴퓨터에서 올립니다. 고르지 않으면 장식 무늬가 나옵니다.',
        },
        {
          kind: 'select',
          path: 'layout',
          label: '나누는 방향',
          options: [
            { value: 'auto', label: '화면에 맞춰 (가로 화면은 좌우 · 세로 화면은 위아래)' },
            { value: 'columns', label: '좌우로 나누기' },
            { value: 'rows', label: '위아래로 나누기' },
          ],
        },
        {
          kind: 'select',
          path: 'mediaSide',
          label: '그림 자리',
          options: [
            { value: 'start', label: '앞 (왼쪽 · 위)' },
            { value: 'end', label: '뒤 (오른쪽 · 아래)' },
          ],
        },
        {
          kind: 'select',
          path: 'share',
          label: '그림 칸 크기',
          help: '1920×1080 으로 만든 HTML 이 반쪽 칸에서 작게 보이면 그림 칸을 키웁니다. 글 칸이 좁아지면 글이 한 단계씩 작아집니다. 이것을 모르는 옛 판 화면은 반반으로 그립니다.',
          options: [
            { value: '40', label: '40% — 글 칸을 넓게' },
            { value: '50', label: '반반 (50%)' },
            { value: '60', label: '60% — 그림·HTML 을 크게' },
            { value: '70', label: '70% — 그림·HTML 을 더 크게' },
          ],
        },
      ],
    },
    {
      title: '글',
      help: '그림 반대쪽 카드에 표시됩니다.',
      fields: [
        { kind: 'text', path: 'badge', label: '배지', max: 20, placeholder: '신제품', help: '비우면 표시되지 않습니다.' },
        { kind: 'text', path: 'title', label: '제목', max: 60 },
        { kind: 'textarea', path: 'body', label: '설명', max: 300, help: '한두 문장을 권장합니다. 길면 멀리서 읽기 어렵습니다.' },
        {
          kind: 'stringList',
          path: 'points',
          label: '핵심 항목',
          max: 4,
          itemMax: 40,
          itemLabel: '항목',
          help: '체크 표시와 함께 나옵니다. 비우면 표시되지 않습니다.',
        },
        { kind: 'text', path: 'note', label: '아래 한 줄', max: 40, placeholder: '문의 안전환경팀 내선 1234', help: '비우면 표시되지 않습니다.' },
      ],
    },
    tickerSection,
  ],

  safety: [
    {
      title: '무재해 목표',
      help: '시작일과 목표 배수만 정하면 화면이 그날 날짜로 목표 기간·달성 예정일·달성 일수·달성률·D-day 를 셉니다. 날이 바뀌어도 고칠 것이 없습니다. 이 템플릿은 에이전트를 새 판으로 올린 화면에만 나갑니다(옛 판 화면은 건너뜁니다).',
      fields: [
        { kind: 'date', path: 'startDate', label: '무재해 시작일' },
        {
          kind: 'select',
          path: 'multiplier',
          label: '목표 배수',
          options: ZERO_ACCIDENT_MULTIPLIERS.map((value) => ({ value, label: `${value}배수` })),
          help: '목표 기간은 앞 배수를 채운 다음 날부터 이 배수를 채우는 날까지입니다 — 1~5배수는 한 해씩, 5→7배수는 두 해, 7→10배수는 세 해입니다.',
        },
      ],
    },
    {
      title: '제목',
      help: '화면 위 큰 카드에 표시됩니다.',
      fields: [
        { kind: 'text', path: 'badge', label: '배지', max: 20, placeholder: '무재해', help: '비우면 표시되지 않습니다.' },
        { kind: 'text', path: 'title', label: '제목', max: 40 },
        { kind: 'text', path: 'team', label: '부서', max: 30, placeholder: '환경안전팀', help: '제목 아래에 나옵니다. 비우면 표시되지 않습니다.' },
      ],
    },
    tickerSection,
  ],

  zones: [
    {
      title: '구역',
      help: '화면을 2~4 구역으로 나눕니다. 구역마다 콘텐츠에 올린 이미지·영상·HTML 을 고르거나(여러 장이면 정한 간격마다 넘깁니다) 글을 씁니다. 영상은 한 화면에 하나 — 그 구역에 혼자 넣고 소리 없이 되풀이합니다. HTML 은 한 화면에 둘까지입니다. 이 템플릿은 에이전트를 새 판으로 올린 화면에만 나갑니다(옛 판 화면은 건너뜁니다).',
      fields: [{ kind: 'zones', path: '', label: '나누는 모양' }],
    },
    tickerSection,
  ],
};

/** 명세가 모든 템플릿을 덮는지 — 타입만으로는 런타임 누락을 못 잡는다. */
export function assertFormsComplete(): void {
  for (const id of TEMPLATE_IDS) {
    if (!TEMPLATE_FORMS[id]) throw new Error(`템플릿 폼 명세 누락: ${id}`);
  }
}
